import React, { Component, Suspense } from 'react';
import { LoadingState } from '@fluid/loading-ui';
import PropTypes from 'prop-types';
import cx from 'classnames';
import WidgetErrorBoundary from './WidgetErrorBoundary';
import WidgetService from './services/WidgetService';
import {
    WIDGET_MODES,
    WIDGET_PROPERTY_DATA_TYPES,
} from './constants/widgetConst';

export default class WidgetRenderer extends Component {
    static contextTypes = {
        fiDomainInfo: PropTypes.object,
    };

    static propTypes = {
        isAdmin: PropTypes.bool,
        widgetPromise: PropTypes.object,
        messages: PropTypes.object.isRequired,
        onWidgetInit: PropTypes.func,
        containerCls: PropTypes.string,
        contextCls: PropTypes.string,
        extraWidgetProperties: PropTypes.object,
        windowDimensions: PropTypes.object,

        fallbackComponent: PropTypes.object,
        layoutOption: PropTypes.object,
        fiDomainInfo: PropTypes.object,

        sourceUrl: PropTypes.string,
        name: PropTypes.string,
        isIFrame: PropTypes.bool,
        widgetProperties: PropTypes.array,
    };

    static defaultProps = {
        fiDomainInfo: WidgetService.getFIDomainInfo(),
    };

    static getDerivedStateFromProps(nextProps) {
        const { widgetProperties } = nextProps;

        return {
            normalizedProperties: WidgetRenderer.getNormalizedProperties(
                widgetProperties,
            ),
        };
    }

    static getNormalizedProperties(widgetProperties) {
        const reducer = (accumulator, currentValue) => {
            const accumulatorClone = { ...accumulator };
            const {
                propertyName,
                propertyType,
                propertyValue,
                propertyValueLocale,
                isLocalized,
            } = currentValue;

            let convertedValue = propertyValue;

            if (
                propertyType === WIDGET_PROPERTY_DATA_TYPES.TEXT &&
                isLocalized
            ) {
                convertedValue = {
                    ...(propertyValueLocale ?? {}),
                    ...(propertyValue && { en_US: propertyValue }),
                };
            } else if (propertyType === WIDGET_PROPERTY_DATA_TYPES.LIST) {
                convertedValue = (propertyValue || '').split(',');
            }

            accumulatorClone[propertyName] = convertedValue;

            return accumulatorClone;
        };

        const currentLocale = window.di?.env?.locale || 'en_US'; // use dbkapi once thats available
        const reducerStartingValue = { locale: currentLocale };

        return (widgetProperties || []).reduce(reducer, reducerStartingValue);
    }

    constructor(props) {
        super(props);
        const { widgetProperties } = this.props;
        this.wrapperElement = React.createRef();
        this.state.normalizedProperties = WidgetRenderer.getNormalizedProperties(
            widgetProperties,
        );
    }

    state = {};

    render() {
        const {
            isAdmin,
            messages,
            sourceUrl,
            isIFrame,
            containerCls,
            windowDimensions,
            extraWidgetProperties,
            widgetProperties,
            name,
            layoutOption,
            fiDomainInfo,
            contextCls,
            ...rest
        } = this.props;
        const WidgetComponent = this.getWidgetComponent();
        const fallbackComponent = this.getFallbackComponent();
        const { normalizedProperties } = this.state;

        let extraProps = {};

        if (sourceUrl) {
            extraProps.sourceUrl = sourceUrl;
            extraProps.layoutOption = layoutOption;
            extraProps.originalWidgetProperties = widgetProperties;
            extraProps.fiDomainInfo = fiDomainInfo;
        } else {
            extraProps = {
                ...rest,
                containerCls,
                wrapperElement: this.wrapperElement,
            };
        }

        return (
            <WidgetErrorBoundary messages={messages}>
                <Suspense fallback={fallbackComponent}>
                    <div
                        className={cx(
                            contextCls,
                            `${containerCls}__widget-container`,
                            'composite-layout-widget',
                        )}
                        ref={this.wrapperElement}
                    >
                        <WidgetComponent
                            mode={isAdmin ? WIDGET_MODES.ADMIN : ''}
                            windowDimensions={windowDimensions}                            
                            name={name}
                            {...normalizedProperties}
                            {...extraProps}
                            {...(extraWidgetProperties || {})}
                        />
                    </div>
                </Suspense>
            </WidgetErrorBoundary>
        );
    }

    getFallbackComponent() {
        const { fallbackComponent } = this.props;

        if (fallbackComponent) {
            return fallbackComponent;
        }

        return <LoadingState />;
    }

    getWidgetComponent() {
        if (!this.widgetComponent) {
            const widgetPromise = this.getWidgetPromise();

            this.widgetComponent = React.lazy(() => {
                return widgetPromise;
            });
        }

        return this.widgetComponent;
    }

    getWidgetPromise() {
        const { widgetPromise, isIFrame } = this.props;

        if (widgetPromise) {
            return widgetPromise;
        }

        const { sourceUrl } = this.props;
        const { fiDomainInfo } = this.context;

        return WidgetService.fetchWidgetComponent(
            sourceUrl,
            fiDomainInfo,
            isIFrame,
        );
    }

    getWidgetElement() {
        return this.wrapperElement ? this.wrapperElement.current : undefined;
    }
}
